import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Upload, Sparkles, CheckCircle2, FileImage, X } from "lucide-react";
import { toast, Toaster } from "sonner";
import api from "../lib/api";

const ALLOWED_EXT = ["png", "jpg", "jpeg", "psd", "doc", "docx", "svg"];
const MAX_MB = 20;

export default function LogoUploadPage() {
    const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
    const [file, setFile] = useState(null);
    const [sending, setSending] = useState(false);
    const [done, setDone] = useState(false);
    const inputRef = useRef(null);
    const update = (k) => (v) => setForm(f => ({ ...f, [k]: v }));

    const pickFile = (f) => {
        if (!f) return;
        const ext = (f.name.split(".").pop() || "").toLowerCase();
        if (!ALLOWED_EXT.includes(ext)) {
            toast.error(`Formaat .${ext} niet toegelaten. Toegestaan: ${ALLOWED_EXT.join(", ")}`);
            return;
        }
        if (f.size > MAX_MB * 1024 * 1024) {
            toast.error(`Bestand te groot (max ${MAX_MB} MB)`);
            return;
        }
        setFile(f);
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim()) { toast.error("Naam en e-mail zijn verplicht"); return; }
        if (!file) { toast.error("Selecteer eerst een bestand"); return; }
        setSending(true);
        try {
            const fd = new FormData();
            fd.append("name", form.name);
            fd.append("email", form.email);
            fd.append("phone", form.phone);
            fd.append("message", form.message);
            fd.append("file", file);
            await api.post("/logo-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setDone(true);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Versturen mislukt — probeer opnieuw");
        } finally { setSending(false); }
    };

    if (done) {
        return (
            <div data-testid="logo-upload-success" className="min-h-[70vh] grid place-items-center px-6 py-20" style={{ background: "var(--bg)" }}>
                <Toaster />
                <div className="max-w-xl text-center">
                    <div className="w-20 h-20 rounded-full mx-auto flex items-center justify-center" style={{ background: "var(--rose-tint)" }}>
                        <CheckCircle2 size={40} style={{ color: "var(--rose-deep)" }} />
                    </div>
                    <p className="label-tiny mt-6" style={{ color: "var(--rose-deep)" }}>★ Logo ontvangen</p>
                    <h1 className="font-display text-5xl mt-3">Bedankt — we hebben je logo binnen.</h1>
                    <p className="mt-5 text-base" style={{ color: "var(--text-2)" }}>
                        We sturen een persoonlijke reactie binnen <strong>24 uur</strong> naar <span className="font-mono-dm">{form.email}</span>.
                    </p>
                    <div className="mt-8 flex gap-3 justify-center">
                        <Link to="/" className="btn-ghost" data-testid="logo-back-home">← Naar de winkel</Link>
                        <Link to="/stickers" className="btn-rose" data-testid="logo-go-stickers">
                            <Sparkles size={14} /> Bekijk stickers
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="logo-upload-page" className="min-h-screen" style={{ background: "var(--bg)" }}>
            <Toaster />
            {/* Hero */}
            <section className="hero-warm border-b border-[var(--border-soft)]">
                <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-16 lg:py-20">
                    <p className="label-tiny" style={{ color: "var(--rose-deep)" }}>★ Stuur ons je logo</p>
                    <h1 className="font-display text-5xl md:text-6xl mt-3 tracking-tight leading-[1.05]">
                        Heb je een <span className="font-script" style={{ color: "var(--rose-deep)" }}>logo</span>?<br />
                        Stuur het ons door.
                    </h1>
                    <p className="mt-6 max-w-xl text-base leading-relaxed" style={{ color: "var(--text-2)" }}>
                        Upload je logo en wij bekijken of we het in vinyl of etsfolie kunnen omzetten.
                        Je krijgt binnen 24 uur een voorstel met prijs en levertijd.
                    </p>
                    <div className="mt-5 inline-flex items-start gap-2 px-3 py-2 rounded-xl max-w-xl" style={{ background: "var(--rose-tint)" }}>
                        <span className="text-base shrink-0" aria-hidden>📎</span>
                        <p className="text-xs leading-relaxed" style={{ color: "var(--text)" }}>
                            Toegelaten formaten: <strong>.png · .jpg · .jpeg · .psd · .doc · .docx · .svg</strong> · max 20 MB
                        </p>
                    </div>
                </div>
            </section>

            {/* Form */}
            <section className="max-w-[900px] mx-auto px-6 lg:px-10 py-14">
                <form onSubmit={submit} className="card-soft p-8 md:p-10 grid grid-cols-1 md:grid-cols-2 gap-5 bg-white">
                    <div>
                        <label className="label-tiny mb-1.5 block">Je naam *</label>
                        <input data-testid="logo-name" className="input-flat" value={form.name}
                            onChange={(e) => update("name")(e.target.value)} placeholder="Lotte Vandenberghe" required />
                    </div>
                    <div>
                        <label className="label-tiny mb-1.5 block">E-mail *</label>
                        <input data-testid="logo-email" type="email" className="input-flat" value={form.email}
                            onChange={(e) => update("email")(e.target.value)} placeholder="jij@example.com" required />
                    </div>
                    <div>
                        <label className="label-tiny mb-1.5 block">Telefoon (optioneel)</label>
                        <input data-testid="logo-phone" className="input-flat" value={form.phone}
                            onChange={(e) => update("phone")(e.target.value)} placeholder="+32 ..." />
                    </div>
                    <div className="md:col-span-2">
                        <label className="label-tiny mb-1.5 block">Bestand *</label>
                        <input ref={inputRef} type="file" accept=".png,.jpg,.jpeg,.psd,.doc,.docx,.svg,image/png,image/jpeg,image/svg+xml,image/vnd.adobe.photoshop,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                            onChange={(e) => pickFile(e.target.files?.[0])}
                            data-testid="logo-file"
                            className="hidden" />
                        {!file ? (
                            <button type="button" onClick={() => inputRef.current?.click()}
                                data-testid="logo-file-pick"
                                className="w-full border-2 border-dashed rounded-2xl p-10 hover:bg-[var(--rose-tint)] transition-colors flex flex-col items-center gap-3"
                                style={{ borderColor: "var(--border)" }}>
                                <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: "var(--rose-tint)" }}>
                                    <Upload size={22} style={{ color: "var(--rose-deep)" }} />
                                </div>
                                <p className="font-display text-xl" style={{ color: "var(--text)" }}>Klik om bestand te kiezen</p>
                                <p className="text-xs" style={{ color: "var(--text-3)" }}>.png · .jpg · .jpeg · .psd · .doc · .docx · .svg · max 20 MB</p>
                            </button>
                        ) : (
                            <div className="rounded-2xl border p-4 flex items-center gap-4" style={{ borderColor: "var(--border)", background: "var(--bg-warm)" }} data-testid="logo-file-selected">
                                <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ background: "var(--rose-tint)" }}>
                                    <FileImage size={20} style={{ color: "var(--rose-deep)" }} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="font-semibold truncate text-sm">{file.name}</p>
                                    <p className="text-xs" style={{ color: "var(--text-2)" }}>{(file.size / 1024).toFixed(0)} KB</p>
                                </div>
                                <button type="button" onClick={() => { setFile(null); if (inputRef.current) inputRef.current.value = ""; }}
                                    data-testid="logo-file-remove"
                                    className="text-xs underline" style={{ color: "var(--rose-deep)" }}>
                                    <X size={14} /> Verwijder
                                </button>
                            </div>
                        )}
                    </div>
                    <div className="md:col-span-2">
                        <label className="label-tiny mb-1.5 block">Bericht (optioneel)</label>
                        <textarea data-testid="logo-message"
                            value={form.message}
                            onChange={(e) => update("message")(e.target.value)}
                            rows={4}
                            placeholder="Vertel waarvoor je het logo wil gebruiken (auto, raam, helm, glas, ...). Eventuele afmetingen of kleur."
                            className="input-flat resize-y" />
                    </div>
                    <div className="md:col-span-2 flex flex-wrap gap-3 items-center pt-2">
                        <button type="submit" disabled={sending || !file} data-testid="logo-submit" className="btn-rose disabled:opacity-60">
                            {sending ? "Versturen..." : <><Upload size={14} /> Stuur logo door →</>}
                        </button>
                        <span className="text-xs" style={{ color: "var(--text-3)" }}>
                            We sturen je binnen 24u een persoonlijk antwoord.
                        </span>
                    </div>
                </form>
            </section>
        </div>
    );
}
